<template>
  <ElConfigProvider :locale="zhCn">
    <div class="childPannel">
      <!-- 搜索框 -->

      <!-- 开始和结束日期选择器在同一行 -->
      <div style="display: flex; align-items: center; width: 100%; margin-bottom: 12px">
        <el-date-picker
          v-model="params.startDate"
          type="date"
          placeholder="选择开始日期"
          style="flex: 1; margin-right: 8px"
        />
        <span style="margin-right: 8px">至</span>
        <el-date-picker
          v-model="params.endDate"
          type="date"
          placeholder="选择结束日期"
          style="flex: 1"
        />
        <el-button
          style="font-size: 15px; width: 10%; height: 90%; margin-left: 8px"
          type="primary"
          @click="onDateSearch"
        >
          搜索
        </el-button>
      </div>

      <!-- 只搜索仍在播报的通告复选框 -->
      <el-checkbox v-model="params.isDate" label="只搜索仍在播报的通告">
        只搜索仍在播报的通告
      </el-checkbox>

      <!-- 渲染过滤后的通告列表 -->
      <el-collapse
        v-if="noticePointList.length"
        class="childPannel-collapse"
        accordion
        v-model="activeNotice"
        @change="onPanelChange()"
      >
        <el-collapse-item v-for="m in noticePointList" :key="m.id" :name="m.id" :title="m.hdtgName">
          <div class="childPannel-collapse-box">
            <div class="childPannel-collapse-box-item">
              <span style="color: rgb(205, 94, 87)">
                {{ formatTimestamp(m.dateStart) }} - {{ formatTimestamp(m.dateEnd) }}
              </span>
            </div>
            <!-- 使用 processHtml 处理 HTML 内容 -->
            <div class="notice-content" v-html="processHtml(m.hdtgText)"></div>
            <div class="childPannel-collapse-box-item">
              <span style="color: #09a986">{{ m.sshd }}</span>
              <span>{{ m.fbdw }}</span>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>

      <!-- 当没有通告时显示提示 -->
      <el-empty v-if="!noticePointList.length" :image-size="100" description="当前内容为空" />
      <el-pagination
        class="childPannel-pagination"
        v-else
        size="small"
        v-model:current-page="params.pageNum"
        :page-size="params.pageSize"
        background
        :pager-count="5"
        @change="getList"
        layout="prev, pager, next"
        :total="total"
      />
    </div>
  </ElConfigProvider>
</template>

<script lang="ts" setup>
import { ref, computed, onUnmounted, onMounted, watch, reactive } from 'vue'
import { dayjs } from 'element-plus'
import zhCn from 'element-plus/es/locale/lang/zh-cn' // 引入Element Plus的中文语言包
import { addProblematicWaterArea } from '@/components/GIS/waterAreaMarker'
import { CesiumObj } from '@/components/GIS/CesiumObj'
import { toCameraPosition } from './utils'
import * as Cesium from 'cesium'
import { getNoticeDataInfo } from '@/apiFzn'
import type { INoticeData } from '@/apiFzn/types'
import type { BaseLayer } from '@/components/Layer'
import type { SecondarywaterLayer } from '@/components/Layer/SecondarywaterLayer'

// 接口联调
const props = defineProps({
  showPannelKey: {
    type: String,
    default: '',
  },
  searchValue: {
    type: String,
    default: '', // 提供默认值
  },
})

const noticePointList = ref<INoticeData[]>([])

const params = reactive({
  pageNum: 1,
  pageSize: 10,
  startDate: undefined,
  endDate: undefined,
  isDate: false,
  name: '',
})
const total = ref(0)

const getList = () => {
  getNoticeDataInfo(params).then((res) => {
    const allNotices = res.rows
    noticePointList.value = allNotices.filter((notice) => {
      const noticeStart = new Date(notice.dateStart).getTime()
      // 筛选通告开始时间在选择器时间范围内的通告
      const withinDateRange =
        (!params.startDate || noticeStart >= new Date(params.startDate).getTime()) &&
        (!params.endDate || noticeStart <= new Date(params.endDate).getTime())
      return withinDateRange
    })
    total.value = res.total
  })
}

const onDateSearch = () => {
  params.pageNum = 1 // 重置页码到第一页
  getList() // 重新获取列表
}

defineExpose({
  getList,
})

watch(
  () => props.searchValue,
  (newVal) => {
    params.name = newVal
    params.pageNum = 1
  },
  { immediate: true }
)

watch(
  () => params.isDate,
  () => {
    params.pageNum = 1 // 重置页码到第一页
    getList() // 重新获取列表
  }
)

const activeNotice = ref<number>()

function formatTimestamp(timestamp: number | string) {
  return dayjs(timestamp).locale('zh-cn').format('YYYY年MM月DD日')
}

let oldActiveNotice: number | undefined = undefined
let lastActivePrimitive: Cesium.GroundPrimitive | null = null

/** 展开通告手风琴的hook */
async function onPanelChange() {
  await removeLastActivePrimitive()

  const newVal = activeNotice.value
  oldActiveNotice = newVal
  if (!newVal) return
  const selectedItem = noticePointList.value.find((item) => item.id == newVal)
  if (!selectedItem) {
    throw new Error('未找到对应的通知点')
  }

  // 调整视角到指定位置
  toCameraPosition({
    lat: selectedItem.geoJson.coordinates[0][0][1],
    lng: selectedItem.geoJson.coordinates[0][0][0],
  })
  const gPrimitive = addProblematicWaterArea(selectedItem.geoJson.coordinates.flat())
  const cesiumInstance = await CesiumObj.getInstance()
  const viewer = cesiumInstance.viewer
  viewer.scene.primitives.add(gPrimitive)
  lastActivePrimitive = gPrimitive
}

/** 移除激活的图层 */
async function removeLastActivePrimitive() {
  const cesiumInstance = await CesiumObj.getInstance()
  const viewer = cesiumInstance.viewer
  if (oldActiveNotice && lastActivePrimitive) {
    viewer.scene.primitives.remove(lastActivePrimitive)
  }
}

onUnmounted(() => {
  removeLastActivePrimitive()
  // CesiumObj.getInstance().then((cesiumInstance) => {
  //   cesiumInstance.layerManager.SecondarywaterLayer.hide()
  // })
})

CesiumObj.getInstance().then((cesiumInstance) => {
  // cesiumInstance.layerManager.SecondarywaterLayer.show()
  ;(cesiumInstance.layerManager.SecondarywaterLayer as SecondarywaterLayer).bringToTop()
})

/** 处理html */
const processHtml = (txt: string) => {
  if (!txt) return ''

  // 清理转义字符
  const rawHtml = txt
    .replace(/\\"/g, '"') // 替换 \" 为 "
    .replace(/\\n/g, '') // 移除 \n
    .replace(/&mdash;/g, '—') // 替换 &mdash; 为 —

  // 移除非标准属性
  const cleanedHtml = rawHtml.replace(/mso-[a-z-]+;/g, '')

  // 处理编码
  return unescape(cleanedHtml)
}
</script>

<style lang="scss" scoped>
@import url('./childPannel.scss');
.notice-content * {
  font-size: 15px !important; /* 强制设置字体大小 */
  line-height: 1.5; /* 可选：调整行高以提升可读性 */
}
</style>
